import { Injectable, ComponentRef, ApplicationRef, createComponent, EnvironmentInjector } from '@angular/core';
import { ConfirmDialogComponent, ConfirmDialogData, ConfirmDialogCloseResult} from '../components/confirm-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private componentRef: ComponentRef<ConfirmDialogComponent> | null = null;

  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector,
  ) {}

  async confirm(data: ConfirmDialogData): Promise<boolean> {
    const result = await this.openDialog(data);
    return result.confirmed;
  }

  /**
   * Confirm dialog with an optional alt action.
   * @returns `'confirm'` | `'alt'` | `false` (cancelled)
   */
  async confirmChoice(data: ConfirmDialogData): Promise<'confirm' | 'alt' | false> {
    const result = await this.openDialog(data);
    if (!result.confirmed) {
      return false;
    }
    return result.choice === 'alt' ? 'alt' : 'confirm';
  }

  /** Opens a confirm dialog with a required text input; returns the trimmed value or null if cancelled. */
  async confirmWithInput(
    data: ConfirmDialogData & { textInput: NonNullable<ConfirmDialogData['textInput']> },
  ): Promise<string | null> {
    const result = await this.openDialog(data);
    if (!result.confirmed) {
      return null;
    }
    return result.value?.trim() ?? null;
  }

  private openDialog(data: ConfirmDialogData): Promise<ConfirmDialogCloseResult> {
    return new Promise((resolve) => {
      const componentRef = createComponent(ConfirmDialogComponent, {
        environmentInjector: this.injector,
      });

      componentRef.instance.data = data;

      const subscription = componentRef.instance.closed.subscribe((result: ConfirmDialogCloseResult) => {
        this.close();
        subscription.unsubscribe();
        resolve(result);
      });

      document.body.appendChild(componentRef.location.nativeElement);
      this.appRef.attachView(componentRef.hostView);

      this.componentRef = componentRef;
    });
  }

  private close(): void {
    if (this.componentRef) {
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }
}

